// Shared render preparation. CAD/reference geometry is never rewritten.
export const RENDER_GEOMETRY_LIMITS = Object.freeze({
  curves: 5000, pointsPerCurve: 8192, totalPoints: 200000,
  dashPatternEntries: 32, dashPaths: 16384, dashSteps: 200000
});
const fail = message => { throw new RangeError(`GPU quality geometry: ${message}`); };
const check = (condition, message) => { if (!condition) fail(message); };
const finite = value => typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1e12;
const positive = value => finite(value) && value > 0;
const point = value => Array.isArray(value) && value.length === 2 && value.every(finite);
const samePoint = (a, b) => a[0] === b[0] && a[1] === b[1];
const near = (a, b) => Math.abs(a - b) <= 1e-9;
const copyPoints = points => {
  check(Array.isArray(points) && points.length >= 2 && points.length <= RENDER_GEOMETRY_LIMITS.pointsPerCurve
    && points.every(point), 'invalid or excessive points');
  return points.map(value => [...value]);
};

function optionsChecked({ assetScale, zoom, maxScreenDeviationCssPx = 0.15 } = {}) {
  check(positive(assetScale) && positive(zoom), 'positive assetScale and zoom required');
  const pixelsPerModelUnit = assetScale * zoom;
  check(positive(pixelsPerModelUnit), 'excessive scale product');
  check(positive(maxScreenDeviationCssPx) && maxScreenDeviationCssPx <= 4, 'screen deviation must be in (0,4] CSS px');
  return { pixelsPerModelUnit, maxScreenDeviationCssPx };
}

function prepare(curve, options) {
  check(curve && typeof curve === 'object' && typeof curve.id === 'string' && curve.id.length > 0
    && curve.id.length <= 256 && typeof curve.closed === 'boolean', 'invalid curve identity/closure');
  const common = { id: curve.id, closed: curve.closed };
  if (curve.visibility !== undefined) {
    check(['visible', 'hidden'].includes(curve.visibility), 'unsupported curve visibility');
    common.visibility = curve.visibility;
  }
  const a = curve.analytic;
  if (curve.kind === 'line') {
    check(a && point(a.start) && point(a.end) && !curve.closed, 'invalid analytic line');
    return { ...common, points: [[...a.start], [...a.end]], approximation: 'analytic-chord-bound', maxScreenDeviationCssPx: 0 };
  }
  if (curve.kind === 'bspline' || curve.kind === 'polyline') {
    const points = copyPoints(curve.displayPoints);
    check(!curve.closed || samePoint(points[0], points.at(-1)), 'closed fallback needs a closed point sequence');
    return { ...common, points, approximation: 'sampled-unverified', maxScreenDeviationCssPx: null,
      fallback: { id: curve.id, kind: curve.kind, reason: 'Existing sampled points have no verified screen-space deviation bound' } };
  }
  const circular = curve.kind === 'circle' || curve.kind === 'circular-arc';
  const elliptic = curve.kind === 'ellipse' || curve.kind === 'elliptic-arc';
  check(circular || elliptic, 'unsupported curve kind');
  check(a && point(a.center) && finite(a.startAngle) && finite(a.endAngle)
    && a.endAngle > a.startAngle && finite(a.normalZ) && near(Math.abs(a.normalZ), 1), 'invalid analytic conic frame');
  const rotation = circular ? a.xAxisAngleRad : a.rotationRad;
  const radiusX = circular ? a.radius : a.majorRadius;
  const radiusY = circular ? a.radius : a.minorRadius;
  check(finite(rotation) && positive(radiusX) && positive(radiusY) && radiusX >= radiusY, 'invalid analytic conic radii/rotation');
  const full = curve.kind === 'circle' || curve.kind === 'ellipse';
  check(curve.closed === full, 'conic kind and closure disagree');
  const start = a.startAngle; const end = a.endAngle; const span = end - start;
  check(span <= Math.PI * 2 + 1e-9 && (!full || near(span, Math.PI * 2)), 'unsupported conic span');
  if (curve.parameterRange !== null) {
    check(Array.isArray(curve.parameterRange) && curve.parameterRange.length === 2
      && curve.parameterRange.every(finite) && near(curve.parameterRange[0], start)
      && near(curve.parameterRange[1], end), 'analytic/native conic domains disagree');
  }
  // The ellipse is a scaled/rotated unit circle. Its largest singular value
  // is majorRadius, so the circular sagitta bounds its chord error as well.
  // Stable inverse of e = 2*R*sin(delta/4)^2, with numerical headroom.
  const radiusScreen = radiusX * options.pixelsPerModelUnit;
  check(positive(radiusScreen), 'excessive screen radius');
  const coordinateGuard = 32 * Number.EPSILON * Math.max(1, Math.abs(a.center[0]), Math.abs(a.center[1]), radiusX)
    * options.pixelsPerModelUnit;
  check(coordinateGuard < options.maxScreenDeviationCssPx * 0.01, 'screen tolerance below coordinate precision');
  const workingError = (options.maxScreenDeviationCssPx - coordinateGuard) * 0.99;
  const angleLimit = Math.min(Math.PI / 2, 4 * Math.asin(Math.sqrt(Math.min(1, workingError / (2 * radiusScreen)))));
  check(positive(angleLimit), 'screen tolerance exceeds numerical precision');
  const segments = Math.max(1, Math.ceil(span / angleLimit));
  check(segments + 1 <= RENDER_GEOMETRY_LIMITS.pointsPerCurve, 'analytic curve point budget exceeded');
  const direction = a.normalZ < 0 ? -1 : 1;
  const cosine = Math.cos(rotation); const sine = Math.sin(rotation);
  let previousParameter = start; let largestAngle = 0;
  const points = Array.from({ length: segments + 1 }, (_, i) => {
    const parameter = i === segments ? end : start + span * i / segments;
    if (i > 0) {
      check(parameter > previousParameter, 'parameter step below numerical precision');
      largestAngle = Math.max(largestAngle, parameter - previousParameter);
    }
    previousParameter = parameter;
    const u = radiusX * Math.cos(parameter); const v = direction * radiusY * Math.sin(parameter);
    const result = [a.center[0] + cosine * u - sine * v, a.center[1] + sine * u + cosine * v];
    check(point(result), 'non-finite or excessive analytic output');
    return result;
  });
  if (full) points[points.length - 1] = [...points[0]];
  // Include an allowed tiny full-circle domain discrepancy in the final chord.
  largestAngle += full ? Math.abs(Math.PI * 2 - span) : 0;
  const deviation = 2 * radiusScreen * Math.sin(largestAngle / 4) ** 2 + coordinateGuard;
  check(Number.isFinite(deviation) && deviation <= options.maxScreenDeviationCssPx, 'requested chord bound cannot be met');
  return { ...common, points, approximation: 'analytic-chord-bound', maxScreenDeviationCssPx: deviation };
}

/** Reprepare at the displayed CSS scale, without using DPR as a geometric scale. */
export function prepareRenderCurve(curve, options) {
  return prepare(curve, optionsChecked(options));
}

/** Fill rings retain their independent sampled/unverified status. */
export function prepareRenderProjection(projection, options) {
  const checked = optionsChecked(options);
  check(projection && Array.isArray(projection.curves) && projection.curves.length <= RENDER_GEOMETRY_LIMITS.curves,
    'projection curve budget exceeded');
  const curves = []; const ids = new Set(); const fallbacks = [];
  let pointCount = 0; let analyticCurveCount = 0; let maxGeneratedDeviationCssPx = 0;
  for (const source of projection.curves) {
    const curve = prepare(source, checked);
    check(!ids.has(curve.id), 'duplicate curve id'); ids.add(curve.id);
    pointCount += curve.points.length;
    check(pointCount <= RENDER_GEOMETRY_LIMITS.totalPoints, 'projection point budget exceeded');
    if (curve.fallback) fallbacks.push({ ...curve.fallback });
    else {
      analyticCurveCount += 1;
      maxGeneratedDeviationCssPx = Math.max(maxGeneratedDeviationCssPx, curve.maxScreenDeviationCssPx);
    }
    curves.push(curve);
  }
  return { curves, report: { curveCount: curves.length, pointCount, analyticCurveCount,
    fallbackCurveCount: fallbacks.length, fallbacks,
    requestedMaxScreenDeviationCssPx: checked.maxScreenDeviationCssPx,
    maxGeneratedDeviationCssPx, fillApproximation: 'sampled-unverified' } };
}

/**
 * Positive dash lengths only. Odd lists repeat twice, as in SVG. Positive
 * offset consumes that distance into the repeated pattern. CSS lengths are
 * converted once using the actual CSS-pixels/model-mm ratio, without DPR.
 * Phase continues through vertices; optional closed paths join across the seam.
 * This operates on the supplied polyline, not on exact CAD arc length.
 */
export function splitDashPath(inputPoints, {
  dash, offset = 0, unit = 'css-px', pixelsPerModelUnit, closed = false
} = {}) {
  const points = copyPoints(inputPoints);
  check(Array.isArray(dash) && dash.length >= 1 && dash.length <= RENDER_GEOMETRY_LIMITS.dashPatternEntries
    && dash.every(positive), 'dash pattern requires bounded positive lengths');
  check(finite(offset) && positive(pixelsPerModelUnit) && ['css-px', 'model-mm'].includes(unit)
    && typeof closed === 'boolean', 'invalid dash options');
  check(!closed || samePoint(points[0], points.at(-1)), 'closed dash path must repeat its start');
  const factor = unit === 'css-px' ? pixelsPerModelUnit : 1;
  const pattern = (dash.length % 2 ? [...dash, ...dash] : dash).map(value => value / factor);
  check(pattern.every(positive), 'dash length outside numeric range');
  const period = pattern.reduce((sum, value) => sum + value, 0);
  check(positive(period) && finite(offset / factor), 'dash period/offset outside numeric range');
  let phase = ((offset / factor) % period + period) % period;
  let patternIndex = 0;
  while (phase >= pattern[patternIndex]) {
    phase -= pattern[patternIndex]; patternIndex = (patternIndex + 1) % pattern.length;
  }
  let remaining = pattern[patternIndex] - phase;
  const paths = []; let active = null; let emittedPoints = 0; let steps = 0;
  const append = coordinates => {
    if (!active) {
      check(paths.length < RENDER_GEOMETRY_LIMITS.dashPaths, 'dash path budget exceeded');
      active = []; paths.push(active);
    }
    if (!active.length || !samePoint(active.at(-1), coordinates)) {
      emittedPoints += 1;
      check(emittedPoints <= RENDER_GEOMETRY_LIMITS.totalPoints, 'dash output point budget exceeded');
      active.push(coordinates);
    }
  };
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]; const b = points[i];
    const dx = b[0] - a[0]; const dy = b[1] - a[1]; const length = Math.hypot(dx, dy);
    if (length === 0) continue;
    let consumed = 0;
    const at = distance => distance === 0 ? [...a] : distance === length ? [...b]
      : [a[0] + dx * distance / length, a[1] + dy * distance / length];
    while (consumed < length) {
      steps += 1; check(steps <= RENDER_GEOMETRY_LIMITS.dashSteps, 'dash traversal budget exceeded');
      const amount = Math.min(remaining, length - consumed);
      const next = amount === length - consumed ? length : consumed + amount;
      check(next > consumed, 'dash step below numerical precision');
      if (patternIndex % 2 === 0) { append(at(consumed)); append(at(next)); }
      consumed = next;
      if (amount === remaining) {
        patternIndex = (patternIndex + 1) % pattern.length; remaining = pattern[patternIndex];
        if (patternIndex % 2 !== 0) active = null;
      } else remaining -= amount;
    }
  }
  if (closed && paths.length > 1 && samePoint(paths[0][0], points[0])
    && samePoint(paths.at(-1).at(-1), points[0])) {
    const last = paths.pop(); paths[0] = [...last, ...paths[0].slice(1)];
  }
  return paths;
}
